import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Upload,
  FolderOpen,
  Folder,
  FolderCog,
  FolderGit,
  FolderKanban,
  FolderLock,
  FolderOpen as FolderOpenIcon,
  FolderPlus,
  FolderSearch,
  FolderSync,
  FolderTree,
  Image as ImageIcon,
  FileText,
  Video,
  Music,
  HardDrive,
  AlertCircle,
  Search,
  Filter,
  CreditCard,
  Zap,
  Shield,
  Cloud,
  Tag,
  Trash2,
  Download,
  Edit,
  X,
  Plus,
  CheckCircle,
  MoreVertical,
  ArrowLeft,
  Check
} from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Folder {
  id: string;
  name: string;
  type: 'videos' | 'images' | 'documents' | 'audio' | 'custom';
  icon: typeof Video;
  color: string;
  count: number;
}

const folderIcons = [
  { icon: Folder, name: 'Basic Folder' },
  { icon: FolderOpenIcon, name: 'Open Folder' },
  { icon: FolderCog, name: 'Settings Folder' },
  { icon: FolderGit, name: 'Git Folder' },
  { icon: FolderKanban, name: 'Kanban Folder' },
  { icon: FolderLock, name: 'Secure Folder' },
  { icon: FolderPlus, name: 'Add Folder' },
  { icon: FolderSearch, name: 'Search Folder' },
  { icon: FolderSync, name: 'Sync Folder' },
  { icon: FolderTree, name: 'Tree Folder' }
];

const folderColors = [
  { name: 'Indigo', value: '#6366F1' },
  { name: 'Blue', value: '#3B82F6' },
  { name: 'Green', value: '#10B981' },
  { name: 'Red', value: '#EF4444' },
  { name: 'Purple', value: '#8B5CF6' },
  { name: 'Pink', value: '#EC4899' },
  { name: 'Yellow', value: '#F59E0B' },
  { name: 'Teal', value: '#14B8A6' }
];

interface LibraryFile {
  id: string;
  name: string;
  type: string;
  size: number;
  url: string;
  thumbnail_url?: string;
  metadata: Record<string, any>;
  tags: string[];
  created_at: string;
  folder_id?: string;
}

const Library = () => {
  const [files, setFiles] = useState<LibraryFile[]>([]);
  const [folders, setFolders] = useState<Folder[]>([
    {
      id: 'videos',
      name: 'Videos',
      type: 'videos',
      icon: Video,
      color: '#8B5CF6',
      count: 0
    },
    {
      id: 'images',
      name: 'Images',
      type: 'images',
      icon: ImageIcon,
      color: '#3B82F6',
      count: 0
    },
    {
      id: 'documents',
      name: 'Documents',
      type: 'documents',
      icon: FileText,
      color: '#10B981',
      count: 0
    },
    {
      id: 'audio',
      name: 'Audio',
      type: 'audio',
      icon: Music,
      color: '#EF4444',
      count: 0
    }
  ]);
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [selectedIcon, setSelectedIcon] = useState<typeof Folder>(Folder);
  const [selectedColor, setSelectedColor] = useState(folderColors[0].value);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadLog, setUploadLog] = useState<Array<{
    type: 'info' | 'error' | 'success';
    message: string;
    timestamp: Date;
    details?: any;
  }>>([]);
  const [showStoragePlansModal, setShowStoragePlansModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<LibraryFile | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [storageStats, setStorageStats] = useState({ total: 5, used: 0 });

  useEffect(() => {
    loadFiles();
  }, []);

  const loadFiles = async () => {
    try {
      setLoading(true);
      const { data: files, error: fetchError } = await supabase
        .from('library_files')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      setFiles(files || []);
      
      // Update folder counts
      setFolders(prev => prev.map(folder => ({
        ...folder,
        count: files?.filter(file => {
          if (folder.type === 'videos') return file.type.startsWith('video/');
          if (folder.type === 'images') return file.type.startsWith('image/');
          if (folder.type === 'documents') return file.type.startsWith('application/');
          if (folder.type === 'audio') return file.type.startsWith('audio/');
          return false;
        }).length || 0
      })));

      // Calculate storage usage
      const usedStorage = files?.reduce((acc, file) => acc + file.size, 0) || 0;
      setStorageStats(prev => ({
        ...prev,
        used: Number((usedStorage / (1024 * 1024 * 1024)).toFixed(2)) // Convert to GB
      }));

    } catch (err) {
      console.error('Error loading files:', err);
      setError(err instanceof Error ? err.message : 'Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = event.target.files?.[0];
      if (!file) {
        setUploadLog(prev => [...prev, {
          type: 'error',
          message: 'No file selected',
          timestamp: new Date()
        }]);
        return;
      }

      setUploading(true);
      setUploadProgress(0);
      setUploadLog(prev => [...prev, {
        type: 'info',
        message: `Starting upload of ${file.name}`,
        timestamp: new Date(),
        details: {
          name: file.name,
          size: formatFileSize(file.size),
          type: file.type
        }
      }]);

      // Upload file to Supabase Storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
      const userId = (await supabase.auth.getUser()).data.user?.id;
      if (!userId) throw new Error('User not authenticated');
      const filePath = `${userId}/${Math.random().toString(36).substring(2)}_${fileName}`;
      
      setUploadLog(prev => [...prev, {
        type: 'info',
        message: 'Uploading file to storage',
        timestamp: new Date(),
        details: { path: filePath, folder: userId }
      }]);

      // Set up storage options
      const options = {
        cacheControl: '3600',
        upsert: false
      };

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('library')
        .upload(filePath, file, options);

      if (uploadError?.message?.includes('duplicate')) {
        setUploadLog(prev => [...prev, {
          type: 'error',
          message: 'A file with this name already exists',
          timestamp: new Date(),
          details: uploadError
        }]);
        throw new Error('A file with this name already exists');
      }

      if (uploadError) {
        setUploadLog(prev => [...prev, {
          type: 'error',
          message: 'Storage upload failed',
          timestamp: new Date(),
          details: uploadError
        }]);
        throw uploadError;
      }
      
      setUploadLog(prev => [...prev, {
        type: 'success',
        message: 'File uploaded to storage successfully',
        timestamp: new Date()
      }]);

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('library')
        .getPublicUrl(filePath);

      setUploadLog(prev => [...prev, {
        type: 'info',
        message: 'Creating database record',
        timestamp: new Date(),
        details: { url: publicUrl }
      }]);

      // Create file record in database without specifying id
      const { data: fileData, error: insertError } = await supabase
        .from('library_files')
        .insert({
          user_id: userId,
          name: file.name,
          type: file.type,
          size: file.size,
          url: publicUrl,
          metadata: {
            lastModified: file.lastModified,
            type: file.type
          },
          tags: []
        })
        .select()
        .single();

      if (insertError) {
        setUploadLog(prev => [...prev, {
          type: 'error',
          message: 'Database insert failed',
          timestamp: new Date(),
          details: insertError
        }]);
        throw insertError;
      }
      
      setUploadLog(prev => [...prev, {
        type: 'success',
        message: 'File record created in database',
        timestamp: new Date(),
        details: { id: fileData.id }
      }]);

      setFiles(prev => [fileData, ...prev]);
      setShowUploadModal(false);

    } catch (err) {
      console.error('Error uploading file:', err);
      setUploadLog(prev => [...prev, {
        type: 'error',
        message: err instanceof Error ? err.message : 'Unknown error occurred',
        timestamp: new Date(),
        details: err
      }]);
      setError(err instanceof Error ? err.message : 'Failed to upload file');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteFile = async (file: LibraryFile) => {
    try {
      // Delete from storage
      const fileName = file.url.split('/').pop();
      if (fileName) {
        const { error: storageError } = await supabase.storage
          .from('library')
          .remove([fileName]);

        if (storageError) throw storageError;
      }

      // Delete from database
      const { error: dbError } = await supabase
        .from('library_files')
        .delete()
        .eq('id', file.id);

      if (dbError) throw dbError;

      setFiles(prev => prev.filter(f => f.id !== file.id));
      setSelectedFile(null);
      setShowDeleteConfirm(false);

    } catch (err) {
      console.error('Error deleting file:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete file');
    }
  };

  const handleDownload = async (file: LibraryFile) => {
    try {
      const response = await fetch(file.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading file:', err);
      setError(err instanceof Error ? err.message : 'Failed to download file');
    }
  };

  const handleCreateFolder = () => {
    if (!newFolderName.trim()) return;
    
    const newFolder: Folder = {
      id: crypto.randomUUID(),
      name: newFolderName.trim(),
      type: 'custom',
      icon: selectedIcon,
      color: selectedColor,
      count: 0
    };

    setFolders(prev => [...prev, newFolder]);
    setNewFolderName('');
    setSelectedIcon(Folder);
    setSelectedColor(folderColors[0].value);
    setShowNewFolderModal(false);
  };

  const handleDeleteFolder = (folderId: string) => {
    setFolders(prev => prev.filter(f => f.id !== folderId));
  };

  const filteredFiles = files.filter(file => {
    const matchesSearch = file.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !selectedType || file.type.startsWith(selectedType);
    const matchesFolder = !selectedFolder || 
      (selectedFolder === 'videos' && file.type.startsWith('video/')) ||
      (selectedFolder === 'images' && file.type.startsWith('image/')) ||
      (selectedFolder === 'documents' && file.type.startsWith('application/')) ||
      (selectedFolder === 'audio' && file.type.startsWith('audio/')) ||
      file.folder_id === selectedFolder;
    const matchesTags = selectedTags.length === 0 || 
      selectedTags.every(tag => file.tags?.includes(tag));
    return matchesSearch && matchesType && matchesTags && matchesFolder;
  });

  const usagePercentage = (storageStats.used / storageStats.total) * 100;

  const getFileIcon = (type: string) => {
    switch (type) {
      case 'image/jpeg':
      case 'image/png':
      case 'image/gif':
        return <ImageIcon className="h-5 w-5 text-blue-500" />;
      case 'video/mp4':
      case 'video/quicktime':
        return <Video className="h-5 w-5 text-purple-500" />;
      case 'application/pdf':
      case 'application/msword':
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        return <FileText className="h-5 w-5 text-green-500" />;
      case 'audio/mpeg':
      case 'audio/wav':
        return <Music className="h-5 w-5 text-red-500" />;
      default:
        return <FileText className="h-5 w-5 text-gray-500" />;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Digital Library</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Manage your digital assets and media files
          </p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowUploadModal(true)}
          className="btn-primary"
        >
          <Upload className="h-4 w-4 mr-2" />
          Upload Files
        </motion.button>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center space-x-2">
          <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400" />
          <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Storage Usage Card */}
      <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <HardDrive className="h-5 w-5 text-indigo-600" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Storage Usage</h2>
            <button
              onClick={() => setShowStoragePlansModal(true)}
              className="ml-2 text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-medium"
            >
              Upgrade Storage
            </button>
          </div>
          <span className="text-sm text-gray-500">Free Plan</span>
        </div>

        <div className="space-y-4">
          <div className="relative pt-1">
            <div className="flex mb-2 items-center justify-between">
              <div>
                <span className="text-xs font-semibold inline-block text-indigo-600">
                  {usagePercentage.toFixed(1)}% Used
                </span>
              </div>
              <div className="text-right">
                <span className="text-xs font-semibold inline-block text-gray-600 dark:text-gray-400">
                  {storageStats.used}GB / {storageStats.total}GB
                </span>
              </div>
            </div>
            <div className="overflow-hidden h-2 text-xs flex rounded bg-indigo-100 dark:bg-indigo-900/20">
              <div
                style={{ width: `${usagePercentage}%` }}
                className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-indigo-600"
              />
            </div>
          </div>

          {usagePercentage > 80 && (
            <div className="flex items-center space-x-2 text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg">
              <AlertCircle className="h-5 w-5" />
              <p className="text-sm">Storage space is running low. Consider upgrading your plan.</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Search and Filters */}
      <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm p-6">
        <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search files..."
              className="pl-10 w-full px-4 py-2 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
            />
          </div>
          <div className="flex space-x-2">
            <select
              value={selectedType || ''}
              onChange={(e) => setSelectedType(e.target.value || null)}
              className="px-4 py-2 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
            >
              <option value="">All Types</option>
              <option value="image">Images</option>
              <option value="video">Videos</option>
              <option value="application">Documents</option>
              <option value="audio">Audio</option>
            </select>
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedType(null);
                setSelectedTags([]);
              }}
              className="px-4 py-2 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-lg text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-dark-600 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Folders Grid */}
      {!selectedFolder && (
        <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
              <FolderOpen className="h-5 w-5 mr-2 text-indigo-600 dark:text-indigo-400" />
              Folders
            </h2>
            <button
              onClick={() => setShowNewFolderModal(true)}
              className="flex items-center space-x-2 text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"
            >
              <Plus className="h-4 w-4" />
              <span>New Folder</span>
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {folders.map((folder, index) => (
              <motion.div
                key={folder.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-50 dark:bg-dark-700 rounded-lg p-6 cursor-pointer group relative hover:shadow-lg transition-all duration-200"
                onClick={() => setSelectedFolder(folder.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-4 rounded-lg" style={{ backgroundColor: `${folder.color}20` }}>
                      <folder.icon className="h-8 w-8" style={{ color: folder.color }} />
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">{folder.name}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {folder.count} {folder.count === 1 ? 'file' : 'files'}
                      </p>
                    </div>
                  </div>
                  
                  {folder.type === 'custom' && (
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteFolder(folder.id);
                        }}
                        className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* File Browser */}
      {selectedFolder && <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-200 dark:border-dark-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setSelectedFolder(null)}
                className="mr-2 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <FolderOpen className="h-5 w-5 text-indigo-600" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {folders.find(f => f.id === selectedFolder)?.name || 'Files'}
              </h2>
            </div>
            <div className="flex items-center space-x-2">
              <button className="text-sm text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400">
                Sort by: Date ↓
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="mt-4 text-gray-500 dark:text-gray-400">Loading files...</p>
            </div>
          ) : filteredFiles.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-500 dark:text-gray-400">No files found</p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-700">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Size</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-dark-700">
                {filteredFiles.map((file) => (
                  <tr
                    key={file.id}
                    className="hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getFileIcon(file.type)}
                        <span className="ml-2 text-sm text-gray-900 dark:text-white">{file.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-500 dark:text-gray-400">{file.type}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-500 dark:text-gray-400">{formatFileSize(file.size)}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(file.created_at).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleDownload(file)}
                          className="p-1 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedFile(file);
                            setShowDeleteConfirm(true);
                          }}
                          className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>}
      
      {/* Storage Plans Modal */}
      {showStoragePlansModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-dark-800 rounded-xl shadow-xl max-w-4xl w-full"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Storage Plans</h2>
                  <p className="mt-2 text-gray-600 dark:text-gray-400">Choose the right storage plan for your needs</p>
                </div>
                <button
                  onClick={() => setShowStoragePlansModal(false)}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Basic Plan */}
                <div className="bg-gray-50 dark:bg-dark-700 rounded-xl p-6 border-2 border-transparent hover:border-indigo-500 transition-all duration-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Basic</h3>
                    <span className="px-2.5 py-1 bg-gray-100 dark:bg-dark-600 text-gray-600 dark:text-gray-300 rounded-full text-xs font-medium">
                      Current Plan
                    </span>
                  </div>
                  <div className="mb-6">
                    <span className="text-3xl font-bold text-gray-900 dark:text-white">Free</span>
                  </div>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                      <Cloud className="h-4 w-4 mr-2 text-indigo-600 dark:text-indigo-400" />
                      5GB Storage
                    </li>
                    <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                      <Zap className="h-4 w-4 mr-2 text-indigo-600 dark:text-indigo-400" />
                      Basic file management
                    </li>
                    <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                      <Shield className="h-4 w-4 mr-2 text-indigo-600 dark:text-indigo-400" />
                      Standard security
                    </li>
                  </ul>
                  <button
                    disabled
                    className="w-full px-4 py-2 bg-gray-100 dark:bg-dark-600 text-gray-500 dark:text-gray-400 rounded-lg cursor-not-allowed"
                  >
                    Current Plan
                  </button>
                </div>

                {/* Pro Plan */}
                <div className="bg-gradient-to-b from-indigo-50 to-white dark:from-indigo-900/20 dark:to-dark-700 rounded-xl p-6 border-2 border-indigo-500 shadow-lg relative">
                  <div className="absolute top-0 right-0 -translate-y-1/2 px-3 py-1 bg-indigo-600 text-white text-xs font-medium rounded-full">
                    Popular
                  </div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Pro</h3>
                  </div>
                  <div className="mb-6">
                    <span className="text-3xl font-bold text-gray-900 dark:text-white">$9.99</span>
                    <span className="text-gray-500 dark:text-gray-400">/month</span>
                  </div>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                      <Cloud className="h-4 w-4 mr-2 text-indigo-600 dark:text-indigo-400" />
                      100GB Storage
                    </li>
                    <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                      <Zap className="h-4 w-4 mr-2 text-indigo-600 dark:text-indigo-400" />
                      Advanced file management
                    </li>
                    <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                      <Shield className="h-4 w-4 mr-2 text-indigo-600 dark:text-indigo-400" />
                      Enhanced security
                    </li>
                  </ul>
                  <button
                    onClick={() => {
                      // TODO: Implement upgrade flow
                      console.log('Upgrade to Pro');
                    }}
                    className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center"
                  >
                    <CreditCard className="h-4 w-4 mr-2" />
                    Upgrade Now
                  </button>
                </div>

                {/* Enterprise Plan */}
                <div className="bg-gray-50 dark:bg-dark-700 rounded-xl p-6 border-2 border-transparent hover:border-indigo-500 transition-all duration-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Enterprise</h3>
                  </div>
                  <div className="mb-6">
                    <span className="text-3xl font-bold text-gray-900 dark:text-white">$29.99</span>
                    <span className="text-gray-500 dark:text-gray-400">/month</span>
                  </div>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                      <Cloud className="h-4 w-4 mr-2 text-indigo-600 dark:text-indigo-400" />
                      1TB Storage
                    </li>
                    <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                      <Zap className="h-4 w-4 mr-2 text-indigo-600 dark:text-indigo-400" />
                      Premium file management
                    </li>
                    <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                      <Shield className="h-4 w-4 mr-2 text-indigo-600 dark:text-indigo-400" />
                      Enterprise-grade security
                    </li>
                  </ul>
                  <button
                    onClick={() => {
                      // TODO: Implement upgrade flow
                      console.log('Upgrade to Enterprise');
                    }}
                    className="w-full px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors flex items-center justify-center"
                  >
                    <CreditCard className="h-4 w-4 mr-2" />
                    Contact Sales
                  </button>
                </div>
              </div>

              <div className="mt-8 p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
                <h4 className="text-sm font-medium text-indigo-900 dark:text-indigo-100 mb-2">All Plans Include</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                    <span className="text-sm text-indigo-700 dark:text-indigo-300">Automatic backups</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                    <span className="text-sm text-indigo-700 dark:text-indigo-300">File versioning</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                    <span className="text-sm text-indigo-700 dark:text-indigo-300">SSL encryption</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                    <span className="text-sm text-indigo-700 dark:text-indigo-300">24/7 support</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-800 rounded-lg shadow-xl max-w-2xl w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Upload File</h3>
                <button
                  onClick={() => setShowUploadModal(false)}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="border-2 border-dashed border-gray-300 dark:border-dark-600 rounded-lg p-8 text-center">
                  <input
                    type="file"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="file-upload"
                  />
                  <label
                    htmlFor="file-upload"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <Upload className="h-12 w-12 text-gray-400 mb-4" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Click to upload or drag and drop
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                      Up to 100MB
                    </span>
                  </label>
                </div>
                
                {/* Upload Log */}
                {uploadLog.length > 0 && (
                  <div className="mt-6 border-t border-gray-200 dark:border-dark-700 pt-4">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Upload Log</h4>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {uploadLog.map((log, index) => (
                        <div
                          key={index}
                          className={`p-2 rounded text-sm ${
                            log.type === 'error'
                              ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                              : log.type === 'success'
                              ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                              : 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span>{log.message}</span>
                            <span className="text-xs opacity-75">
                              {log.timestamp.toLocaleTimeString()}
                            </span>
                          </div>
                          {log.details && (
                            <pre className="mt-1 text-xs overflow-x-auto">
                              {JSON.stringify(log.details, null, 2)}
                            </pre>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {uploading && (
                  <div className="space-y-2">
                    <div className="h-2 bg-gray-200 dark:bg-dark-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-indigo-600 transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                      Uploading... {uploadProgress}%
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedFile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20">
                <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
                Delete File
              </h3>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                Are you sure you want to delete "{selectedFile.name}"? This action cannot be undone.
              </p>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteFile(selectedFile)}
                className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* New Folder Modal */}
      {showNewFolderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Create New Folder</h3>
                <button
                  onClick={() => setShowNewFolderModal(false)}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="folderName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Folder Name
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      id="folderName"
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      className="block w-full rounded-md border-gray-300 dark:border-dark-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white sm:text-sm"
                      placeholder="Enter folder name"
                    />
                  </div>
                </div>
                
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Choose Icon
                  </label>
                  <div className="grid grid-cols-5 gap-2">
                    {folderIcons.map((iconOption, index) => {
                      const IconComponent = iconOption.icon;
                      return (
                        <button
                          key={index}
                          type="button"
                          onClick={() => setSelectedIcon(iconOption.icon)}
                          className={`p-3 rounded-lg flex items-center justify-center transition-colors ${
                            selectedIcon === iconOption.icon
                              ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 ring-2 ring-indigo-500'
                              : 'bg-gray-50 dark:bg-dark-700 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-600'
                          }`}
                          title={iconOption.name}
                        >
                          <IconComponent className="h-6 w-6" />
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Choose Color
                  </label>
                  <div className="grid grid-cols-8 gap-2">
                    {folderColors.map((color, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => setSelectedColor(color.value)}
                        className={`w-8 h-8 rounded-full transition-all ${
                          selectedColor === color.value ? 'ring-2 ring-offset-2 ring-gray-400 dark:ring-gray-600' : ''
                        }`}
                        style={{ backgroundColor: color.value }}
                        title={color.name}
                      >
                        {selectedColor === color.value && (
                          <Check className="h-4 w-4 text-white mx-auto" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowNewFolderModal(false)}
                    className="px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateFolder}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700"
                  >
                    Create Folder
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default Library